/**
 * /sales/ — content.
 *
 * Copy is carried over verbatim from the Tilda export
 * (src/data/tilda/24777883.json); only the layout was rebuilt for the Dashly
 * design system. Images were pulled off the Tilda CDN into static/sales/ and
 * re-encoded, so the page no longer depends on Tilda in any way.
 *
 * One link changed: the fourth step pointed at /autotracking/, which returns
 * 404 in production. It now points at /lead-profile/, the page that replaced it.
 */

export const heroData = {
  subtitle:
    "Automate qualification and scoring to prioritize deals and work with those that matter most",
  cta: "Start converting for free",
  image: "/sales/hero.avif",
  imageAlt: "Sales conversations arriving from several channels",
};

/** Standalone heading block that sat between the hero and the feature list. */
export const featuresHeading =
  "How a sales engagement platform helps your sales team";

/** Every feature block on this page repeats the same CTA. */
export const featureCta = "Start converting for free";

export interface Feature {
  title: string;
  points: string[];
  image: string;
  imageAlt: string;
  /** Image on the left instead of the right. */
  reversed?: boolean;
}

export const featuresData: Feature[] = [
  {
    title:
      "Live chat for sales that closes 7x more deals",
    points: [
      "Collect the contact info and route the conversations to the needed specialist with the live chat on your website",
      "Unify your communications via messengers, email and the livechat into one shared inbox",
      "The visitor can start the conversation on your website and continue wherever they want",
    ],
    image: "/sales/omnichannel.avif",
    imageAlt: "Conversation continued across channels in one inbox",
  },
  {
    title: "Lead routing that sends hot leads straight to your CRM",
    points: [
      "Qualify website visitors with bots and convert them into a sales pipeline",
      "Keep visitor's data and conversation history with them",
      "Hand the hottest leads off to Sales team and provide Marketing team with the leads to nurture",
      "Store the contacts and their data inside the company, sales reps' phones is not a good place to store them",
    ],
    image: "/sales/crm.webp",
    imageAlt: "Qualified lead handed off to the CRM",
    reversed: true,
  },
  {
    title: "Shared inbox for sales teams: every conversation in one place",
    points: [
      "Let people start the conversation with your company in the channel they prefer - your team will cover these from one inbox",
      "All the conversation history will be stored in one place",
      "Look through the paid ad sources to know what triggered the visitors enter the website",
    ],
    image: "/sales/workflow.avif",
    imageAlt: "Shared inbox with conversation history and lead sources",
  },
  {
    title: "Website visitor tracking that personalizes every offer",
    points: [
      "Track all the website visitors behaviour: which page they are mostly interested in and which offer works better",
      "Empower conversations with the personalized experience",
      "Analyze the data about visitors and amplify the website offers",
    ],
    image: "/sales/personalize.avif",
    imageAlt: "Personalized offer built from visitor behaviour",
    reversed: true,
  },
  {
    title: "CRM integration that keeps your sales stack in sync",
    points: [
      "Unite Dashly with your sales team toolkit",
      "Learn the leads' sources, and scale the most profitable of them",
      "Track your pipeline and quickly fix the bottlenecks",
    ],
    image: "/sales/integrations.avif",
    imageAlt: "Logos of the tools Dashly integrates with",
  },
];

export const seatsData = {
  title: "Unlimited seats for your whole sales team",
  text: "Pre-qualify leads and prioritize your work with prospects. With unlimited seats, invite the whole team",
  cta: "Start converting for free",
  image: "/sales/team.avif",
  imageAlt: "Sales team members",
};

export interface Step {
  title: string;
  image: string;
  text?: string;
  link?: { label: string; href: string };
}

export const stepsData = {
  title:
    "5 steps to set up inbound lead qualification for sales",
  cta: "Start converting for free",
  steps: [
    {
      title: "Set up live chat on your website",
      text: "Set up the live chat on the website and collect more conversations than phone calls",
      image: "/sales/step-live-chat.avif",
      link: { label: "Read more", href: "/live-chat/" },
    },
    {
      title: "Unify your channels in one shared inbox",
      text: "Unify your communications channels into one shared inbox",
      image: "/sales/step-inbox.avif",
      link: { label: "Read more", href: "/live-chat/" },
    },
    {
      title: "Qualify visitors with a sales chatbot",
      text: "Add a chatbot to qualify the visitors and hand the hottest leads off to Sales and let Marketing team nurture the rest",
      image: "/sales/step-chatbot.avif",
      link: { label: "Read more", href: "/custom-chatbot/" },
    },
    {
      title: "Track visitor behaviour and lead sources",
      text: "Learn what people do, where they come from and personalize the communication",
      image: "/sales/step-tracking.avif",
      // was /autotracking/, which 404s in production
      link: { label: "Read more", href: "/lead-profile/" },
    },
    {
      title: "Sell from the Dashly mobile app",
      text: "Agents can communicate with customers via Dashly mobile application and send knowledge base articles straight from the app on the go Download Dashly mobile app and stay in touch with your customers from anywhere in the world",
      image: "/sales/step-mobile-app.avif",
    },
  ] as Step[],
  apps: [
    {
      href: "https://itunes.apple.com/ru/app/carrrot/id1417779506?l=en&mt=8",
      image: "/sales/badge-appstore.svg",
      alt: "Download Dashly on the App Store",
    },
    {
      href: "https://play.google.com/store/apps/details?id=io.carrrot.CarrrotApp",
      image: "/sales/badge-googleplay.svg",
      alt: "Get Dashly on Google Play",
    },
  ],
};

export const finalCtaData = {
  title: "Boost revenue with a sales engagement platform built for inbound",
  lines: ["7 days of free trial. No credit card required"],
  cta: "Start converting for free",
};

export const REGISTER_URL = "/panel/unauthorized/register";
